import type { Attachment, Root } from "../../domain/app-types.ts";
import type { Todo } from "../../domain/entities.ts";
import type { StoredEntity } from "../../infrastructure/storage/indexed-db.ts";
import { duplicateTask } from "./task-duplication-controller.ts";
import { transferSubtask } from "./subtask-transfer-controller.ts";

export interface TaskOperationsWorkflowCapabilities {
  entities(rootId?: string): Promise<StoredEntity[]>;
  roots(): Root[];
  canEdit(todo: Todo, rootId: string): boolean;
  actorId(): string;
  randomId(): string;
  changeTodo(id: string, rootId: string, actorId: string, update: (todo: Todo) => void): Promise<unknown>;
  changeTodoBatch?(items: Array<{ id: string; rootId: string; mutate(todo: Todo): void }>, actorId: string): Promise<unknown>;
  request(path: string, init?: RequestInit): Promise<Response | null>;
  icon(name: string): string;
  toast(message: string, error?: boolean): void;
  markLocal(): void;
  render(): Promise<void>;
  undo(message: string, action: () => void | Promise<void>): void;
  sync(): Promise<void>;
  openClone(rootId: string, categoryId: string | null, cloneId: string): Promise<void>;
  loadAttachments(taskId: string): void;
  online(): boolean;
}

export function createTaskOperationsWorkflow(capabilities: TaskOperationsWorkflowCapabilities) {
  async function copyAttachments(sourceTaskId: string, destinationTaskId: string, quiet = false): Promise<{ copied: number; failed: number }> {
    const listResponse = await capabilities.request(`/api/v1/tasks/${sourceTaskId}/attachments`, { credentials: "include" });
    if (!listResponse?.ok) {
      if (quiet) throw new Error("Could not check source attachments");
      return { copied: 0, failed: 0 };
    }
    const attachments = await listResponse.json() as Attachment[];
    let copied = 0;
    for (const attachment of attachments) {
      const response = await capabilities.request(`/api/v1/tasks/${sourceTaskId}/attachments/${attachment.id}`, { credentials: "include" });
      if (!response?.ok) continue;
      const data = new FormData();
      data.append("file", new File([await response.blob()], attachment.file_name, { type: attachment.content_type }));
      const uploaded = await capabilities.request(`/api/v1/tasks/${destinationTaskId}/attachments`, { method: "POST", credentials: "include", body: data });
      if (uploaded?.ok) copied += 1;
    }
    if (copied) {
      if (!quiet) {
        capabilities.toast(`${copied} ${copied === 1 ? "attachment" : "attachments"} copied`);
        capabilities.loadAttachments(destinationTaskId);
      }
    }
    if (copied < attachments.length && !quiet) capabilities.toast(`${attachments.length - copied} ${attachments.length - copied === 1 ? "attachment was" : "attachments were"} not copied`, true);
    return { copied, failed: attachments.length - copied };
  }

  async function transfer(sourceTaskId: string, subtaskId: string): Promise<void> {
    await transferSubtask(sourceTaskId, subtaskId, {
      entities: capabilities.entities, roots: capabilities.roots, canEdit: capabilities.canEdit, actorId: capabilities.actorId,
      randomId: capabilities.randomId, changeTodo: capabilities.changeTodo, icon: capabilities.icon, toast: capabilities.toast,
      setLocal: capabilities.markLocal, render: capabilities.render, undo: capabilities.undo, sync: capabilities.sync,
    });
  }

  async function duplicate(sourceTaskId: string): Promise<void> {
    await duplicateTask(sourceTaskId, {
      entities: capabilities.entities, roots: capabilities.roots, canEdit: capabilities.canEdit, actorId: capabilities.actorId,
      randomId: capabilities.randomId, changeTodo: capabilities.changeTodo, changeTodoBatch: capabilities.changeTodoBatch, icon: capabilities.icon, toast: capabilities.toast,
      setLocal: capabilities.markLocal, openClone: capabilities.openClone, sync: capabilities.sync, copyAttachments, online: capabilities.online,
    });
  }

  return { copyAttachments, transfer, duplicate };
}
